<template>
  <div class="mt-4">
    <v-row class="fill-height justify-center">
      <v-col>
        <v-form
          ref="form"
          lazy-validation
          @keydown.enter="submit"
          @submit.prevent="submit"
          class="py-4"
        >
          <div class="d-flex">
            <v-textarea
              id="emails-area"
              v-model="model.emails"
              filled
              outlined
              dense
              label="Email Addresses"
              :rules="emailRules"
              @input="$refs.form.validate()"
              auto-grow
              autofocus
              :hint="emails_placeholder"
              :persistent-hint="true"
              clearable
              :placeholder="emails_placeholder"
              persistent-placeholder
            />
            <tooltip-modal>
              We will send notifications to the email addresses specified
            </tooltip-modal>
          </div>
          <div class="d-flex">
            <v-textarea
              id="topic_arns-area"
              v-model="model.topic_arns"
              filled
              outlined
              dense
              label="Topic ARN's"
              :rules="snsRules"
              auto-grow
              @input="$refs.form.validate()"
              :hint="arns_placeholder"
              :placeholder="arns_placeholder"
              :persistent-hint="true"
              clearable
            />
            <tooltip-modal>
              We will send a SNS (Simple Notification Service) to the Topic
              ARN's (Amazon Resource Name) specified. The following are general
              formats for ARNs:<br /><br />
              arn:partition:service:region:account-id:resource-id<br />
              arn:partition:service:region:account-id:resource-type/resource-id<br />
              arn:partition:service:region:account-id:resource-type:resource-id<br />
            </tooltip-modal>
          </div>
        </v-form>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import TooltipModal from "@/components/shared/TooltipModal";
import _ from "lodash";
import { mapGetters } from "vuex";

export default {
  props: {
    defaultModel: {
      type: Object,
      default: () => {
        return {
          emails: "",
          topic_arns: "",
        };
      },
    },
    monitor: {},
  },
  components: {
    TooltipModal,
  },
  data() {
    const emails = this.getEmails();
    const topic_arns = this.getSnsTopics();

    return {
      model: {
        emails: emails || this.defaultModel.emails,
        topic_arns: topic_arns || this.defaultModel.topic_arns,
      },
      emails_placeholder: `Separate multiple email addresses with commas`,
      arns_placeholder: `Separate multiple topic arn's with commas`,
    };
  },
  mounted() {
    this.$emit("mounted");
  },
  created() {
    if (!this.model.email) {
      this.model.email = _.get(this.user_details, "email", null);
    }
  },
  computed: {
    ...mapGetters(["user_details"]),

    emailRules() {
      return [
        (v) =>
          v || this.model.topic_arns
            ? true
            : "Email address or SNS Topic ARN required",
        (v) => {
          if (v) {
            const emails = v.split(",");
            const badEmails = [];
            const emailPattern =
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

            emails.forEach((email) => {
              const e = email.trim();

              if (e && !emailPattern.test(e)) {
                badEmails.push(email);
              }
            });

            if (badEmails.length === 1) {
              return `Invalid email: ${badEmails[0]}`;
            } else if (badEmails.length > 1) {
              return `Invalid emails: ${badEmails.join(", ")}`;
            }
          }

          return true;
        },
        (v) => {
          if (v) {
            const emails = v.split(",");
            const badEmails = [];

            emails.forEach((email) => {
              const e = email.trim();

              if (e.length > 128) {
                badEmails.push(e);
              }
            });

            if (badEmails.length === 1) {
              return `Email must have 128 characters or less: ${badEmails[0]}`;
            } else if (badEmails.length > 1) {
              return `The following email addresses must have 128 characters or less: ${badEmails.join(
                ", "
              )}`;
            }
          }

          return true;
        },
      ];
    },
    snsRules() {
      return [
        (v) =>
          v || this.model.emails ? true : "SNS Topic ARN or Email required",
        (v) => {
          if (v) {
            const arns = v.split(",");
            const badArns = [];
            const arnRegex =
              /^(arn:(?:aws|aws-cn|aws-us-gov):[\w\d-]+:[\w\d-]*:\d{0,12}:[\w\d-]*\/?[\w\d-]*)(\/.*)?.*$/;

            arns.forEach((arn) => {
              const a = arn.trim();
              if (a && !arnRegex.test(a)) {
                badArns.push(a);
              }
            });

            if (badArns.length === 1) {
              return `Invalid Topic ARN: ${badArns[0]}`;
            } else if (badArns.length > 1) {
              return `Invalid Topic ARN's: ${badArns.join(", ")}`;
            }
          }

          return true;
        },
      ];
    },
  },
  watch: {
    model: {
      deep: true,
      handler(newVal, oldVal) {
        // console.log(`model change=${JSON.stringify(newVal)}`);
        setTimeout(
          (() => {
            // find all invalid inputs using this method
            // since the form.value field that supposedly
            // states if the form is valid doesn't seem to
            // work with more complex rules like the
            // emailRules and snsRules within this
            // component
            const invalidInputs = this.$refs.form.inputs.filter(
              (input) => !input.valid
            );

            this.$emit("change", newVal, {
              // valid: this.$refs.form.value, // form.value signifies form validity, commented out since it doesn't work with the startDateRules/endDateRules
              valid: invalidInputs.length === 0,
              dirty:
                JSON.stringify(newVal) !== JSON.stringify(this.defaultModel),
            });
          }).bind(this),
          50
        );
      },
    },
  },
  methods: {
    reset() {
      this.$refs.form.reset();

      /* need to give time for form to reset then reset values back to defaults */
      this.$nextTick(() => {
        this.model = { ...this.defaultModel };
      });
    },

    getMonitorActions() {
      if (this.monitor?.actions && Array.isArray(this.monitor?.actions)) {
        return this.monitor?.actions;
      }

      return null;
    },

    getMonitorNotifications() {
      const actions = this.getMonitorActions();
      if (
        actions &&
        actions.notifications &&
        Array.isArray(actions.notifications)
      ) {
        return actions.notifications;
      }

      return null;
    },

    getEmails() {
      const notifications = this.getMonitorNotifications();
      if (Array.isArray(notifications) && notifications.length > 0) {
        const emails = [];
        notifications.forEach((notification) => {
          if (notification.type === "email" && notification.address) {
            emails.push(notification.address);
          }
        });

        return emails;
      }

      return null;
    },

    getSnsTopics() {
      const notifications = this.getMonitorNotifications();
      if (Array.isArray(notifications) && notifications.length > 0) {
        const topics = [];
        notifications.forEach((notification) => {
          if (notification.type === "sns" && notification.topic_arn) {
            topics.push(notification.topic_arn);
          }
        });

        return topics;
      }

      return null;
    },
  },
};
</script>
